import React from 'react';
import { Avatar, Box, SvgIcon, Tooltip, Typography } from '@mui/material';
import { PlusIcon } from 'components/SVG';
import {
  SparkAccordion,
  SparkButton,
  SparkCircularProgress,
  SparkDataTable,
} from 'sharedComponents/UI';
import { Column } from 'sharedComponents/UI/SparkDataTable';
import { useWorkspaceRecommendedMemories } from 'hooks/Workspace/useWorkspaceRecommendedMemories';
import { formatDateAndTime } from 'utilities/time';
import { useDialog, useResize } from 'hooks/common';
import CreateMemoryModal from 'components/Memories/Modals/CreateMemoryModal';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { SPARK_ROUTES } from 'constants/routes';
import FieldSkeleton from 'sharedComponents/Skeleton/FieldSkeleton';
import { useStatusBarStore } from 'stores';
import SparkOptionsKnowledgeModal from 'components/common/SparkOptionsKnowledgeModal';

type KnowledgeTabProps = {
  workspaceID: string | undefined;
  isParentWorkspace?: boolean;
};

function extractNumber(str: string): number {
  const match = str.match(/\((\d+)%\)/);
  return match ? +match[1] : 0;
}

export type Row = {
  id: string;
  description: string;
  createdAt: string;
  importance: string;
  name: string;
  status: string;
  statusIconURL: string;
  statusTextColour: string;
  sparkID?: string;
  memoryID?: string;
  imageURL: string;
  categoryID?: string;
  tabIndex: string;
};
type MemoryStatusProps = {
  iconUrl: string;
  status: string;
  statusColor: string;
  renderText: boolean;
};
const MemoryStatus = ({
  iconUrl,
  status,
  statusColor,
  renderText,
}: MemoryStatusProps) => {
  return (
    <Box display='flex' alignItems='center' gap={2}>
      {renderText ? (
        <Avatar sx={{ width: 32, height: 32 }} src={iconUrl} alt={status} />
      ) : (
        <Tooltip title={status} enterTouchDelay={0}>
          <Avatar sx={{ width: 32, height: 32 }} src={iconUrl} alt={status} />
        </Tooltip>
      )}
      {renderText && (
        <Typography
          sx={{
            color: statusColor,
          }}
        >
          {status}
        </Typography>
      )}
    </Box>
  );
};

function KnowledgeTab({
  workspaceID,
  isParentWorkspace = true,
}: KnowledgeTabProps) {
  const { workspace: workSpaceRecommendedMemories } =
    useWorkspaceRecommendedMemories(workspaceID);
  const isRunning = useStatusBarStore((state) => state.isRunning);
  const statusBarType = useStatusBarStore((state) => state.statusBarType);
  const percentageComplete = useStatusBarStore(
    (state) => state.percentageComplete
  );

  const { isMobile } = useResize();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const recommendedMemoryCategoryTabIndex = searchParams.get(
    'recommendedMemoryCategoryTabIndex'
  );

  const {
    open: isCreateMemoryModalOpen,
    openDialog: openCreateMemoryModal,
    closeDialog: closeCreateMemoryModal,
  } = useDialog();
  const [selectedMemoryData, setSelectedMemoryData] =
    React.useState<Row | null>(null);
  const handleUploadMemoryModal = (
    rowData: Row,
    categoryID: string | undefined
  ) => {
    setSelectedMemoryData({ ...rowData, categoryID });
    openCreateMemoryModal();
  };
  const handleCloseMemoryModal = () => {
    // setSelectedMemoryData(null);
    closeCreateMemoryModal();
  };
  const isAdaptiveLayout = isParentWorkspace || isMobile;

  const Header = (
    <Box
      display='flex'
      alignItems='center'
      justifyContent='space-between'
      marginBottom={2}
      width='100%'
    >
      <Typography
        variant='body2'
        sx={{
          color: '#404040',
          fontFamily: 'Mont-regular',
        }}
      >
        Upload documents or information that SparkUp can reference in inputs and
        outputs
      </Typography>
      <SparkButton
        variant='contained'
        onClick={openCreateMemoryModal}
        startIcon={
          <SvgIcon
            sx={{
              '& path': {
                fill: 'white',
              },
              stroke: 'white',
              paddingTop: '5px',
            }}
          >
            <PlusIcon color='white' />
          </SvgIcon>
        }
        sx={{
          px: isMobile ? 3 : '',
        }}
      >
        <Typography variant='body1'>
          {isMobile ? 'Add' : 'Add Knowledge'}
        </Typography>
      </SparkButton>
    </Box>
  );

  const handleCreateClick = (row: Row) => {
    navigate(
      `${SPARK_ROUTES.SPARK_RUNS}/${row.sparkID}?recommendedmemoryid=${row.id}&callbackURL=${pathname}?recommendedMemoryCategoryTabIndex=${row?.tabIndex}`
    );
  };

  const columns: Column<Row>[] = [
    {
      field: 'status',
      headerText: 'Status',
      flex: isAdaptiveLayout ? 1.5 : 1,
      filterAble: true,
      sortable: true,
      cellRenderer: (row) => {
        return (
          <MemoryStatus
            iconUrl={row.statusIconURL}
            status={row.status}
            statusColor={row.statusTextColour}
            renderText={isParentWorkspace && !isMobile}
          />
        );
      },
    },
    {
      field: 'name',
      headerText: 'Name',
      flex: 3,
      filterAble: true,
      sortable: true,
      cellRenderer: (row) => (
        <Box textAlign={isAdaptiveLayout ? 'unset' : 'left'}>
          <Typography>{row.name}</Typography>
          <Typography
            variant='body2'
            sx={{
              color: '#BDBDBD',
              pt: 0.5,
            }}
          >
            {formatDateAndTime(row.createdAt, 'DD MMM YYYY HH:mm:ss')}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'importance',
      headerText: 'Importance',
      filterAble: true,
      sortable: true,
      justifyContent: isAdaptiveLayout ? 'flex-start' : 'space-around',
      comparator: (a, b) => {
        const importanceA = a.importance;
        const importanceB = b.importance;
        return extractNumber(importanceA) - extractNumber(importanceB);
      },
      flex: 2,
      cellRenderer: (row) => {
        return <Typography>{row.importance}</Typography>;
      },
    },
    {
      field: 'actions',
      headerText: '',
      flex: 2,
      alignItems: 'flex-end',
      cellRenderer: (row) => {
        const showProgressBar =
          statusBarType === 'memoryCreation' &&
          !!isRunning &&
          row.name === selectedMemoryData?.name;

        return (
          <SparkOptionsKnowledgeModal
            showProgressBar={showProgressBar}
            percentageComplete={percentageComplete}
            row={row}
            handleUploadMemoryModal={handleUploadMemoryModal}
            handleCreateClick={handleCreateClick}
            navigate={navigate}
            isMobile={isMobile}
          />
        );
      },
    },
  ];

  const recommendMemories =
    workSpaceRecommendedMemories?.meta?.recommendedMemories?.categories;
  return (
    <Box width='100%'>
      {isParentWorkspace && Header}
      {recommendMemories ? (
        recommendMemories?.map((category, tabIndex) => (
          <Box
            mb={2}
            key={category?.id}
            border={(theme) => `1px solid ${theme.palette.borderColor}`}
            pr={0.2}
            borderRadius={3}
          >
            <SparkAccordion
              title={category?.name}
              initialExpand={
                recommendedMemoryCategoryTabIndex
                  ? recommendedMemoryCategoryTabIndex === `${tabIndex}`
                  : false
              }
              sx={{
                background: 'white',
                border: (theme) => `1px solid ${theme.palette.borderColor}`,
                width: '100%',
                textAlign: 'left',
              }}
              accordionTitleProps={{
                fontFamily: 'Mont-semibold',
                variant: 'h5',
                sx: {
                  textAlign: 'left',
                  width: '100%',
                },
              }}
              icon={
                <SparkCircularProgress
                  value={category?.percentageComplete}
                  color={category?.percentageCompleteColour}
                />
              }
              summaryStyles={{ cursor: 'pointer' }}
            >
              <Box p={2}>
                <SparkDataTable<Row>
                  columns={columns}
                  rows={
                    category.memories.map((memory) => ({
                      ...memory,
                      categoryID: category.categoryID,
                      tabIndex: `${tabIndex}`,
                    })) || []
                  }
                  isRowClickable={false}
                />
              </Box>
            </SparkAccordion>
          </Box>
        ))
      ) : (
        <FieldSkeleton />
      )}
      {isCreateMemoryModalOpen && (
        <CreateMemoryModal
          open={isCreateMemoryModalOpen}
          onClose={handleCloseMemoryModal}
          selectedMemoryData={selectedMemoryData}
        />
      )}
    </Box>
  );
}

export default KnowledgeTab;
